import React, { useState, useEffect } from "react";
import { Card, Heading, Stack, Layout } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import InspectionElement from "../../types/InspectionElement";
import ElementAsset from "../../types/ElementAsset";
import SimpleImageViewer from "../SimpleImageViewer";
import styles from "./SelectableImageGallery.module.css";

interface Props {
    data: InspectionElement;
    selectedImageIds?: string[];
    onSelectionChange: (selectedIds: string[]) => void;
}

export default function SelectableImageGallery({ 
    data, 
    selectedImageIds = [], 
    onSelectionChange 
}: Props) {
    const { t } = useTranslation("common");
    const [localSelectedIds, setLocalSelectedIds] = useState<string[]>(selectedImageIds);
    const [viewerImage, setViewerImage] = useState<ElementAsset | null>(null);
    const [showViewer, setShowViewer] = useState(false);

    // Sync localSelectedIds with selectedImageIds whenever it changes
    useEffect(() => {
        // Only include IDs that actually exist in the current data
        const validImageIds = data.Images ? data.Images.map(img => img.id) : [];
        const validSelectedIds = selectedImageIds.filter(id => validImageIds.includes(id));
        setLocalSelectedIds(validSelectedIds);
    }, [selectedImageIds, data.Images]);

    const handleImageToggle = (imageId: string) => {
        const newSelection = localSelectedIds.includes(imageId)
            ? localSelectedIds.filter(id => id !== imageId)
            : [...localSelectedIds, imageId];
        
        setLocalSelectedIds(newSelection);
        onSelectionChange(newSelection);
    };

    const handleSelectAll = () => {
        const allImageIds = data.Images.map(img => img.id);
        const areAllCurrentlySelected = localSelectedIds.length === data.Images.length && data.Images.length > 0;
        const newSelection = areAllCurrentlySelected ? [] : allImageIds;
        setLocalSelectedIds(newSelection);
        onSelectionChange(newSelection);
    };

    const areAllSelected = localSelectedIds.length === data.Images.length && data.Images.length > 0;

    const handleViewImage = (image: ElementAsset, e: React.MouseEvent) => {
        e.stopPropagation();
        setViewerImage(image);
        setShowViewer(true);
    };

    const closeViewer = () => {
        setShowViewer(false);
        setViewerImage(null);
    };

    if (!data.Images || data.Images.length === 0) {
        return (
            <div className={styles.noImages}>
                <p>No images available for selection</p>
            </div>
        );
    }

    return (
        <div className={styles.galleryContainer}>
            {viewerImage && (
                <SimpleImageViewer
                    image={viewerImage}
                    active={showViewer}
                    onClose={closeViewer}
                />
            )}
            <Layout>
                <Layout.Section>
                    <Card>
                        <div className={styles.padding}>
                            <Stack distribution="equalSpacing" alignment="center">
                                <Stack>
                                    <Heading>Photos</Heading>
                                </Stack>
                                <Stack>
                                    <div className={styles.controls}>
                                        <input
                                            type="checkbox"
                                            id="selectAll"
                                            checked={areAllSelected}
                                            onChange={handleSelectAll}
                                        />
                                        <label htmlFor="selectAll" className={styles.selectAllLabel}>
                                            {t("inspection.photos.select_all")}
                                        </label>
                                        <span className={styles.selectedCount}>
                                            {localSelectedIds.length} of {data.Images.length} selected
                                        </span>
                                    </div>
                                </Stack>
                            </Stack>
                            
                            <div className={styles.imageGrid}>
                                {data.Images.map((image: ElementAsset) => (
                                    <div 
                                        key={image.id} 
                                        className={styles.imageItem}
                                        onClick={() => handleImageToggle(image.id)}
                                    >
                                        <div 
                                            className={`${styles.imageContainer} ${
                                                localSelectedIds.includes(image.id) ? styles.selected : ''
                                            }`}
                                        >
                                            <img 
                                                src={image.url} 
                                                alt={image.name}
                                                className={styles.image}
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = '/images/Image-not-found.png';
                                                }}
                                            />
                                            <div className={styles.overlay}>
                                                <input
                                                    type="checkbox"
                                                    checked={localSelectedIds.includes(image.id)}
                                                    onChange={() => handleImageToggle(image.id)}
                                                    className={styles.checkbox}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                            <div className={styles.viewButton}>
                                                <button
                                                    onClick={(e) => handleViewImage(image, e)}
                                                    className={styles.viewIcon}
                                                    aria-label="View image"
                                                >
                                                    <img
                                                        src="/images/preview_icon.svg"
                                                        alt="View"
                                                        width="16"
                                                        height="16"
                                                    />
                                                </button>
                                            </div>
                                            {localSelectedIds.includes(image.id) && (
                                                <div className={styles.selectedIndicator}>
                                                    <img src="/images/Checklist-Tick.svg" alt="Selected" />
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles.imageName}>
                                            {image.name}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                </Layout.Section>
            </Layout>
        </div>
    );
}
